<template>
  <div class="result-page">
    <div class="result-top">
      <div class="result-title">
        <img
          src="@/assets/icons/i-return.png"
          alt="返回"
          title="返回"
          style="width: 36px; height: 36px; cursor: pointer"
          @click="$emit('back')"
        />
        <div class="result-name">{{ docName }}</div>
        <span class="result-badge">生成结果</span>
      </div>
      <div class="result-actions">
        <button
          type="button"
          class="btn primary"
          :disabled="downloading"
          @click="downloadWord"
        >
          <svg class="icon sm"><use href="#eb-download" /></svg>下载文书
        </button>
      </div>
    </div>

    <div class="result-workspace">
      <!-- 记录模式：真实文书文件（详情 fileDownloadUrl）编辑器只读渲染 -->
      <section v-if="record && record.id" class="doc-view">
        <div v-if="docLoading" class="view-loading">文书加载中…</div>
        <DocxEditor
          v-if="docBuffer"
          class="doc-editor"
          :i18n="zhCN"
          :document-buffer="docBuffer"
          :document-name="docName"
          read-only
          mode="viewing"
          :show-toolbar="false"
          :show-menu-bar="false"
          :show-file-open="false"
          :show-ruler="false"
          :show-outline="false"
          @ready="onEditorReady"
        />
      </section>
      <!-- 兜底：演示纸面预览 -->
      <section v-else class="doc-view">
        <div class="doc-canvas">
          <PaperPreview />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: "GenerateResult" });
import { ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { DocxEditor, provideLocale } from "@eigenpal/docx-editor-vue";
import { zhCN } from "@eigenpal/docx-editor-i18n";
import PaperPreview from "./PaperPreview.vue";
import {
  saveIndictmentDoc,
  getIndictmentConversationDetail,
  downloadIndictmentConversation,
} from "@/api/elementBased";

provideLocale(zhCN);

defineEmits(["back"]);

// materials：首页上传材料返回的会话实体列表（含 id/fileName）；
// 纯文字生成流程没有材料，回落演示文案与演示下载提示。
// record：记录查看模式（聊天记录卡片「查看文书」进入），{ id, name }
const props = defineProps({
  materials: { type: Array, default: () => [] },
  record: { type: Object, default: null },
});

// 文书名：记录名 / 材料文件名驱动，都没有时回落演示文案
const docName = computed(() => {
  const base = props.record?.name || props.materials[0]?.fileName;
  return base
    ? `${String(base).replace(/\.[^.]+$/, "")} · 要素式起诉状`
    : "张某与王某民间借贷纠纷一案 · 要素式起诉状";
});

// ---------- 记录模式：真实文书文件只读渲染 ----------
// Detail 取 fileDownloadUrl 对应文件（直连失败回落 Download 接口），
// buffer 交给 @eigenpal/docx-editor 只读渲染（合同审查 DocPanel 同款，
// 表格/勾选框保真度比 docx-preview 好）
const docBuffer = ref(null);
const docLoading = ref(false);

const loadRecordDoc = async () => {
  const id = props.record?.id;
  if (!id) return;
  docLoading.value = true;
  try {
    let buffer = null;
    const detail = await getIndictmentConversationDetail(id);
    const url =
      detail?.data?.fileDownloadUrl ?? detail?.data?.FileDownloadUrl ?? "";
    if (url) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        buffer = await res.arrayBuffer();
      } catch (err) {
        console.warn(
          "[GenerateResult] fileDownloadUrl 直连失败，改走下载接口:",
          err,
        );
      }
    }
    if (!buffer) {
      const blob = await downloadIndictmentConversation(id);
      buffer = await blob.arrayBuffer();
    }
    docBuffer.value = buffer; // 编辑器挂载渲染，@ready 后关 loading
  } catch (err) {
    console.error("[GenerateResult] 文书加载失败:", err);
    message.error("文书加载失败，请稍后重试");
    docLoading.value = false;
  }
};
const onEditorReady = () => {
  docLoading.value = false;
};
onMounted(loadRecordDoc);

// 下载文书：记录查看模式用记录 id；流程模式用材料会话 id
const downloading = ref(false);
const downloadWord = async () => {
  if (props.record?.id) {
    if (downloading.value) return;
    downloading.value = true;
    try {
      await saveIndictmentDoc({
        id: props.record.id,
        name: props.record.name || docName.value,
      });
      message.success("文件下载成功");
    } catch (err) {
      console.error("[GenerateResult] 下载文书失败:", err);
      message.error("文书下载失败，请稍后重试");
    } finally {
      downloading.value = false;
    }
    return;
  }
  const material = props.materials[0];
  if (!material?.id) return message.info("已生成并下载 Word 文书（演示）");
  if (downloading.value) return;
  downloading.value = true;
  try {
    await saveIndictmentDoc({ id: material.id, name: material.fileName });
    message.success("文件下载成功");
  } catch (err) {
    console.error("[GenerateResult] 下载文书失败:", err);
    message.error("文书下载失败，请稍后重试");
  } finally {
    downloading.value = false;
  }
};
</script>

<style scoped>
.result-page {
  height: calc(100vh - 112px);
  min-height: 640px;
  background: #f3f6fb;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- 顶栏 ---------- */
.result-top {
  height: 54px;
  min-height: 54px;
  background: #fff;
  border: 1px solid #e0e7f0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex: none;
  box-shadow: 0 2px 7px rgba(28, 48, 78, 0.018);
}
.result-title {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.result-name {
  font-size: 15px;
  font-weight: 760;
  color: #22324a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-badge {
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: #edf4ff;
  color: #2f6bff;
  font-size: 10px;
  font-weight: 750;
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex: none;
}
.result-actions {
  display: flex;
  gap: 8px;
  flex: none;
}
.result-actions .btn {
  height: 34px;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
}

/* ---------- 工作区 ---------- */
.result-workspace {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: hidden;
}
.doc-view {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f4f7fb;
  border: 1px solid #dfe6ef;
  border-radius: 15px;
  overflow: hidden;
  position: relative;
}
.doc-canvas {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 24px 28px 38px;
  background: #f4f7fb;
}

/* 真实文书只读渲染：docx-editor 撑满工作区，内部滚动 */
.doc-editor {
  flex: 1;
  min-height: 0;
}
:deep(.docx-editor-scroll-container) {
  padding: 20px 0;
  background: #f4f7fb;
}
.view-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  color: #65758e;
  font-size: 13px;
  background: rgba(244, 247, 251, 0.75);
}

/* ---------- 通用 ---------- */
.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}
.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
  cursor: pointer;
}
.btn.primary {
  background: #1768ff;
  border-color: #1768ff;
  color: #fff;
  box-shadow: 0 3px 8px rgba(23, 104, 255, 0.12);
}
.btn.primary:hover {
  background: #0e59db;
}
</style>
